import { defaultPortfolioBenchmarkPolicy } from "../portfolio-performance.js";
import {
  PLANNED_EXIT_ACTIONS_V2,
  PLANNED_EXIT_QUANTITY_MODES_V2,
  PLANNED_EXIT_TRIGGER_TYPES_V2,
  TRADE_HORIZONS_V2,
} from "./trading.js";
import {
  PORTFOLIO_POLICY_ASSET_CLASSES_V2,
  PORTFOLIO_POLICY_MAXIMUM_PLANNED_EXITS_V2,
} from "./policy-definition.js";
import type { PortfolioPolicyDraftInputV2, PortfolioPolicyDraftValidationV2 } from "./policy-lifecycle.js";
import type { PortfolioPolicyVersionReadModelV2 } from "./policy-lifecycle-read-models.js";
import {
  addDecimals,
  canonicalDecimal,
  compareDecimal,
  currency,
  multiplyDecimals,
  sameJson,
  subtractDecimals,
  text,
} from "./policy-lifecycle-read-primitives.js";

type ActivationEvidenceV2 = NonNullable<PortfolioPolicyVersionReadModelV2["activationEvidence"]>;

/**
 * Financial-semantic boundary for immutable governing versions only.
 * Draft parsing intentionally remains shape-only so incomplete drafts can be saved.
 */
export function isGoverningPortfolioPolicySemanticsV2(options: {
  draft: PortfolioPolicyDraftInputV2;
  validation: PortfolioPolicyDraftValidationV2;
  activationEvidence: ActivationEvidenceV2;
  bookBaseCurrency?: string;
}): boolean {
  try {
    const { draft, validation, activationEvidence } = options;
    if (!currency(draft.baseCurrency) || (options.bookBaseCurrency !== undefined
      && draft.baseCurrency !== options.bookBaseCurrency) || draft.allowFractionalQuantity === null
      || !coreDecimals(draft) || !capitalBasis(draft, activationEvidence)
      || !plannedFlows(draft) || !horizons(draft) || !assets(draft, activationEvidence)
      || !exits(draft) || !benchmark(draft) || !sourceCosts(draft, activationEvidence)) return false;
    return sameJson(validation.dollarPreview, expectedDollarPreview(draft, activationEvidence));
  } catch {
    return false;
  }
}

function coreDecimals(draft: PortfolioPolicyDraftInputV2): boolean {
  const positivePercentages = [draft.maxOpenRiskPct, draft.maxTradeRiskPct, draft.maxPositionPct,
    draft.maxSectorExposurePct, draft.maxCorrelatedExposurePct, draft.maxPortfolioDrawdownPct];
  if (!positivePercentages.every((value) => percentage(value, false))
    || !percentage(draft.minimumCashReservePct, true)
    || !nonnegative(draft.minimumRewardToRisk) || !nonnegative(draft.slippageAllowancePerUnit)) return false;
  const [openRisk, tradeRisk, position, sector, correlated] = positivePercentages as string[];
  return compareDecimal(tradeRisk!, openRisk!) <= 0 && compareDecimal(position!, sector!) <= 0
    && compareDecimal(position!, correlated!) <= 0;
}

function capitalBasis(draft: PortfolioPolicyDraftInputV2, evidence: ActivationEvidenceV2): boolean {
  if (draft.capitalBasis === "OWNER_DECLARED_CAPITAL") {
    return positive(draft.declaredCapital) && evidence.capitalBaseSnapshot === null;
  }
  if (draft.capitalBasis !== "CURRENT_EQUITY" || draft.declaredCapital !== null
    || evidence.capitalBaseSnapshot === null) return false;
  return positive(evidence.capitalBaseSnapshot.value);
}

function plannedFlows(draft: PortfolioPolicyDraftInputV2): boolean {
  if (draft.plannedCapitalFlows.length > 50) return false;
  const sequences = new Set<number>();
  for (const flow of draft.plannedCapitalFlows) {
    if (!Number.isSafeInteger(flow.sequence) || flow.sequence < 1 || sequences.has(flow.sequence)
      || !positive(flow.amount) || flow.currency !== draft.baseCurrency || !calendarDate(flow.plannedFor)
      || (flow.note !== null && (flow.note !== flow.note.trim() || flow.note.length < 1 || flow.note.length > 500))) return false;
    sequences.add(flow.sequence);
  }
  return true;
}

function horizons(draft: PortfolioPolicyDraftInputV2): boolean {
  const policy = draft.horizonPolicy;
  if (policy === null || !TRADE_HORIZONS_V2.includes(policy.defaultHorizon)
    || policy.allocations.length !== TRADE_HORIZONS_V2.length) return false;
  const seen = new Set<string>();
  let total = "0";
  for (const row of policy.allocations) {
    if (!TRADE_HORIZONS_V2.includes(row.horizon) || seen.has(row.horizon)
      || !percentage(row.targetAllocationPct, true)
      || (!row.enabled && compareDecimal(row.targetAllocationPct, "0") !== 0)) return false;
    seen.add(row.horizon);
    total = addDecimals(total, row.targetAllocationPct);
  }
  return seen.size === TRADE_HORIZONS_V2.length && compareDecimal(total, "1") === 0
    && policy.allocations.some((row) => row.horizon === policy.defaultHorizon && row.enabled);
}

function assets(draft: PortfolioPolicyDraftInputV2, evidence: ActivationEvidenceV2): boolean {
  const policy = draft.assetAllocationPolicy;
  if (policy === null || policy.bands.length !== PORTFOLIO_POLICY_ASSET_CLASSES_V2.length) return false;
  const support = new Map(evidence.assetClassSupportSnapshot.map((row) => [row.assetClass, row.supportState]));
  const seen = new Set<string>();
  let total = "0";
  for (const row of policy.bands) {
    const decimals = [row.minimumAllocationPct, row.targetAllocationPct, row.maximumAllocationPct];
    const unsupported = support.get(row.assetClass) === "RECORD_ONLY";
    if (!PORTFOLIO_POLICY_ASSET_CLASSES_V2.includes(row.assetClass) || seen.has(row.assetClass)
      || !decimals.every((value) => percentage(value, true))
      || compareDecimal(row.minimumAllocationPct, row.targetAllocationPct) > 0
      || compareDecimal(row.targetAllocationPct, row.maximumAllocationPct) > 0
      || support.get(row.assetClass) === undefined
      || (unsupported && row.enabled)
      || ((!row.enabled || unsupported)
        && decimals.some((value) => compareDecimal(value, "0") !== 0))) return false;
    seen.add(row.assetClass);
    total = addDecimals(total, row.targetAllocationPct);
  }
  const cash = policy.bands.find((row) => row.assetClass === "CASH");
  return seen.size === PORTFOLIO_POLICY_ASSET_CLASSES_V2.length && support.size === seen.size
    && compareDecimal(total, "1") === 0 && cash !== undefined
    && compareDecimal(cash.targetAllocationPct, draft.minimumCashReservePct!) >= 0;
}

function exits(draft: PortfolioPolicyDraftInputV2): boolean {
  const value = draft.exitConstraints;
  return value !== null && enumSubset(value.allowedQuantityModes, PLANNED_EXIT_QUANTITY_MODES_V2)
    && enumSubset(value.allowedTriggerTypes, PLANNED_EXIT_TRIGGER_TYPES_V2)
    && enumSubset(value.allowedPostExecutionActions, PLANNED_EXIT_ACTIONS_V2)
    && Number.isSafeInteger(value.maximumPlannedExits) && value.maximumPlannedExits >= 1
    && value.maximumPlannedExits <= PORTFOLIO_POLICY_MAXIMUM_PLANNED_EXITS_V2
    && value.requireInitialStop && !value.allowStopWideningAfterEntry;
}

function benchmark(draft: PortfolioPolicyDraftInputV2): boolean {
  const value = draft.benchmarkPolicy;
  if (value === null) return false;
  const truth = defaultPortfolioBenchmarkPolicy();
  return value.schemaVersion === truth.schemaVersion && value.primaryBenchmark === truth.primaryBenchmark
    && enumSubset(value.diagnosticBenchmarks, truth.diagnosticBenchmarks)
    && value.cashBenchmark === truth.cashBenchmark && value.snapshotCadence === truth.snapshotCadence
    && value.returnBasis === truth.returnBasis && value.portfolioMeasurement === truth.portfolioMeasurement
    && value.ownerOutcomeMeasurement === truth.ownerOutcomeMeasurement && value.costBasis === truth.costBasis
    && value.benchmarkSelection === truth.benchmarkSelection && value.appendOnly === truth.appendOnly;
}

function sourceCosts(draft: PortfolioPolicyDraftInputV2, evidence: ActivationEvidenceV2): boolean {
  const policy = draft.sourceCostPolicy;
  const snapshot = evidence.sourceCostSnapshot;
  if (policy === null || policy.brokerSourceId !== snapshot.brokerSourceId) return false;
  if (policy.brokerSourceId === null) {
    return policy.feeAssumptionState === "UNKNOWN" && snapshot.feeScheduleRef === null
      && snapshot.feeEvidenceState === "UNKNOWN";
  }
  if (!text(policy.brokerSourceId)) return false;
  return snapshot.feeScheduleRef === null
    ? policy.feeAssumptionState === "UNKNOWN" && snapshot.feeEvidenceState === "UNKNOWN"
    : policy.feeAssumptionState === "KNOWN_SCHEDULE" && snapshot.feeEvidenceState === "KNOWN_SCHEDULE";
}

function expectedDollarPreview(draft: PortfolioPolicyDraftInputV2, evidence: ActivationEvidenceV2) {
  const capital = draft.capitalBasis === "CURRENT_EQUITY"
    ? evidence.capitalBaseSnapshot!.value : draft.declaredCapital!;
  const reserve = multiplyDecimals(capital, draft.minimumCashReservePct!);
  return {
    status: "COMPLETE",
    currency: draft.baseCurrency,
    capitalBaseAmount: capital,
    maxOpenRiskAmount: multiplyDecimals(capital, draft.maxOpenRiskPct!),
    maxTradeRiskAmount: multiplyDecimals(capital, draft.maxTradeRiskPct!),
    maxPositionAmount: multiplyDecimals(capital, draft.maxPositionPct!),
    maxSectorExposureAmount: multiplyDecimals(capital, draft.maxSectorExposurePct!),
    maxCorrelatedExposureAmount: multiplyDecimals(capital, draft.maxCorrelatedExposurePct!),
    maxPortfolioDrawdownAmount: multiplyDecimals(capital, draft.maxPortfolioDrawdownPct!),
    minimumCashReserveAmount: reserve,
    capitalAfterMinimumCashReserve: subtractDecimals(capital, reserve),
  };
}

function percentage(value: unknown, zeroAllowed: boolean): value is string {
  return canonicalDecimal(value) && compareDecimal(value, "1") <= 0
    && compareDecimal(value, "0") >= (zeroAllowed ? 0 : 1);
}
function positive(value: unknown): value is string {
  return canonicalDecimal(value) && compareDecimal(value, "0") > 0;
}
function nonnegative(value: unknown): value is string {
  return canonicalDecimal(value) && compareDecimal(value, "0") >= 0;
}
function enumSubset<T extends string>(values: readonly T[], allowed: readonly T[]): boolean {
  return values.length > 0 && new Set(values).size === values.length && values.every((value) => allowed.includes(value));
}
function calendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const parsed = Date.parse(`${value}T00:00:00.000Z`);
  return Number.isFinite(parsed) && new Date(parsed).toISOString().slice(0, 10) === value;
}
